import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift
} from '@floating-ui/dom';
import { FiscalMonth } from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import { CompareService } from '@portal/app/shared/services/compare.service';
import {
  FiscalDatePresetService,
  PresetOption
} from '@portal/app/shared/services/fiscal-date-preset.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@Component({
  selector: 'portal-fiscal-calendar',
  templateUrl: './portal-fiscal-calendar.component.html',
  styleUrls: ['./portal-fiscal-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PortalFiscalCalendarComponent implements OnInit, OnDestroy {
  @ViewChild('calendarInputWrapper') calendarInputWrapper?: ElementRef;
  @ViewChild('fiscalCalendarPopup') fiscalCalendarPopup?: ElementRef;

  readonly label = input('Time Period');
  readonly startDate = input<string>('');
  readonly endDate = input<string>('');
  readonly previousStartDate = input<string | null>(null);
  readonly previousEndDate = input<string | null>(null);
  readonly months = input<FiscalMonth[]>([]);
  readonly minDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly maxDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly relativeDate = input<string | null>(null);
  readonly showOnlyCalendarPopup = input<boolean>(false);
  readonly datePickerInputHeight = input<string>('2rem');
  readonly isOpen = input<boolean>(false);
  readonly hideAfterApply = input<boolean>(true);
  readonly disabled = input<boolean>(false);

  @Output() readonly dateChanged = new EventEmitter<{
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
    presetValue: string;
  }>();

  @Output() readonly cancelled = new EventEmitter<void>();

  showCalendar = false;
  forceCompareOn: boolean | undefined = undefined;
  compareToggledOn = false;
  compareEnabled = true;

  private cleanupPopup: (() => void) | null = null;
  private wasDisabled = false;

  private appliedDatePreset = 'custom';
  private datePresetOptions: readonly PresetOption[] = [];
  private dateToShowSingleRange = '';
  private dateToShowCompareRange = '';

  private readonly displayFormat = 'MMM D, YYYY';
  private readonly compareService = inject(CompareService);
  private readonly fiscalDatePresetService = inject(FiscalDatePresetService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.openCalendar();
      } else {
        this.closeCalendar();
      }
      this.cdr.markForCheck();
    });

    // Mirrors legacy behavior: the popup stays open while filters update
    // and closes once the update resolves (disabled true -> false).
    effect(() => {
      const isDisabled = this.disabled();
      if (!isDisabled && this.wasDisabled) {
        this.closeCalendar();
      }
      this.wasDisabled = isDisabled;
    });
  }

  ngOnInit(): void {
    this.compareToggledOn = this.compareService.compare;
    this.compareEnabled = this.compareService.compareIsAvailable;
    if (!this.compareEnabled) {
      this.compareToggledOn = false;
    }
    this.datePresetOptions = [
      ...this.fiscalDatePresetService.datePresetOptions
    ].sort((a, b) => a.order - b.order);

    const savedPreset = this.relativeDate();
    if (savedPreset && savedPreset !== 'custom_range') {
      const match = this.datePresetOptions.find((o) => o.value === savedPreset);
      if (match) {
        this.appliedDatePreset = savedPreset;
      }
    }

    this.updateLabelsFromInputs();
  }

  toggleCalendar(event?: MouseEvent): void {
    event?.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.showCalendar ? this.closeCalendar() : this.openCalendar();
  }

  onOutsideCompareToggle(value: boolean): void {
    this.forceCompareOn = value;
    this.compareToggledOn = value;
    this.openCalendar();
  }

  getDatePickerLabel(): string {
    if (this.appliedDatePreset !== 'custom') {
      const option = this.datePresetOptions.find(
        (o) => o.value === this.appliedDatePreset
      );
      if (option) return option.label;
    }

    const cached = this.compareToggledOn
      ? this.dateToShowCompareRange
      : this.dateToShowSingleRange;
    if (cached) return cached;

    const start = this.startDate();
    const end = this.endDate();
    if (start && end) {
      const s = dayjs(start).utc(true).format(this.displayFormat);
      const e = dayjs(end).utc(true).format(this.displayFormat);
      return `${s} - ${e}`;
    }
    return '';
  }

  onPopupDateChanged(event: {
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
    presetValue: string;
  }): void {
    this.appliedDatePreset = event.presetValue;
    this.compareToggledOn = this.compareService.compare;
    this.updateLabelsFromDates(
      event.startDate,
      event.endDate,
      event.previousStartDate,
      event.previousEndDate
    );
    if (this.hideAfterApply()) {
      this.closeCalendar();
    }
    this.dateChanged.emit(event);
  }

  onPopupCancelled(): void {
    this.compareToggledOn = this.compareService.compare;
    this.closeCalendar();
    this.cancelled.emit();
  }

  ngOnDestroy(): void {
    this.cleanupPopup?.();
  }

  private openCalendar(): void {
    if (this.showCalendar) return;
    this.showCalendar = true;
    setTimeout(() => this.positionPopup());
  }

  protected closeCalendar(): void {
    this.showCalendar = false;
    this.forceCompareOn = undefined;
    this.cleanupPopup?.();
    this.cleanupPopup = null;
  }

  private positionPopup(): void {
    if (this.showOnlyCalendarPopup()) return;
    const anchor = this.calendarInputWrapper?.nativeElement;
    const popup = this.fiscalCalendarPopup?.nativeElement;
    if (!anchor || !popup) return;

    this.cleanupPopup = autoUpdate(anchor, popup, async () => {
      const { x, y } = await computePosition(anchor, popup, {
        placement: 'bottom-end',
        middleware: [offset(7), flip(), shift({ padding: 7 })]
      });
      Object.assign(popup.style, { left: `${x}px`, top: `${y}px` });
    });
  }

  private updateLabelsFromInputs(): void {
    const start = this.startDate();
    const end = this.endDate();
    if (start && end) {
      const s = dayjs(start).utc(true).format(this.displayFormat);
      const e = dayjs(end).utc(true).format(this.displayFormat);
      this.dateToShowSingleRange = `${s} - ${e}`;

      const ps = this.previousStartDate();
      const pe = this.previousEndDate();
      if (ps && pe) {
        const psFmt = dayjs(ps).utc(true).format(this.displayFormat);
        const peFmt = dayjs(pe).utc(true).format(this.displayFormat);
        this.dateToShowCompareRange = `${s} - ${e} | ${psFmt} - ${peFmt}`;
      } else {
        this.dateToShowCompareRange = this.dateToShowSingleRange;
      }
    }
  }

  private updateLabelsFromDates(
    startDate: string,
    endDate: string,
    previousStartDate: string | null,
    previousEndDate: string | null
  ): void {
    const s = dayjs(startDate).utc(true).format(this.displayFormat);
    const e = dayjs(endDate).utc(true).format(this.displayFormat);
    this.dateToShowSingleRange = `${s} - ${e}`;

    if (previousStartDate && previousEndDate) {
      const ps = dayjs(previousStartDate).utc(true).format(this.displayFormat);
      const pe = dayjs(previousEndDate).utc(true).format(this.displayFormat);
      this.dateToShowCompareRange = `${s} - ${e} | ${ps} - ${pe}`;
    } else {
      this.dateToShowCompareRange = this.dateToShowSingleRange;
    }
  }
}
